import {Typography} from '@mui/material';
import {t} from 'i18next';
import _, {noop} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  createSTR,
  deleteSTR,
  finaliseSTR,
  getSTRDetail,
} from '../../../api/STRService';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {
  setCurrentFieldTemplateId,
  setStrReportId,
} from '../../../redux/slices/strSlice';
import {RootState} from '../../../redux/store';
import {Terms} from '../../../utils/createSTRUtils';
import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import AurButton from '../../common/aur-button/AurButton';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurPopup from '../../common/aur-popup/AurPopup';
import AurStepper from '../../common/aur-stepper-tab/AurStepperTab';
import ResultSetting, {ResultSettingsType} from './tabs/ResultSetting';
import ReviewSearch, {STRCreateReviewDataType} from './tabs/ReviewSearch';
import ScopeContent, {
  MAX_CHARACTER_COUNT,
  ScopeContentType,
} from './tabs/ScopeContent';
import TermsContent from './tabs/terms-content/TermsContent';
import {colorArray, EciHomePageTabs} from '../../../constants/constants';
import {runSTRTerms, getSearchTermRunResults} from '../../../api/EcaServices';
import {getAPIErrorDetail, showErrorToast} from '../../../api/utils';
import navConstants from '../../../constants/navConstants';
import {
  extractSelectedNodeId,
  unwrapQueryText,
  wrapQueryBeforeSymbol,
} from '../../../utils/utils';
import {STRTermRunResultsType} from '../view-str/ViewSTR';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

enum STR_STEPS {
  SCOPE_CONTENT = 0,
  ADD_TERMS = 1,
  RESULT_SETTING = 2,
  RUN_SEARCH = 3,
}

export enum STR_FLOW {
  CREATE = 'Create',
  DRAFT = 'Draft',
  EDIT = 'Edit',
  ADDTERMS = 'AddTerms',
}

export const updateTermIds = (firstList: Terms[], secondList: Terms[]) => {
  return secondList.map((item) => {
    if (!item.termId) {
      const match = firstList.find((f) => f.term === item.term);
      if (match?.termId) {
        return {...item, termId: match.termId};
      }
    }
    return item;
  });
};

const CreateSTR = () => {
  const stringPath = 'eca.searchTermReport.';
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const locationQueryParam = new URLSearchParams(location.search);
  const currentFlow = locationQueryParam.get('Flow');

  const isStepDisabled = useCallback(() => {
    if (currentFlow === STR_FLOW.ADDTERMS) return true;
  }, [currentFlow]);

  const getStepLabel = useCallback(() => {
    // Default label is for CREATE flow
    let label = 'steps.summary.create';

    // Substitute EDIT label for EDIT flow
    if (currentFlow === STR_FLOW.EDIT) {
      label = 'steps.summary.edit';
    }

    return t(stringPath + label);
  }, [currentFlow]);

  const steps = [
    {label: t(stringPath + 'steps.Scope'), disabled: isStepDisabled()},
    {label: t(stringPath + 'steps.AddTerms')},
    {label: t(stringPath + 'steps.ResultSettings'), disabled: isStepDisabled()},
    {label: getStepLabel(), disabled: isStepDisabled()},
  ];

  const [activeStep, setActiveStep] = useState(-1);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [resultSettingsData, setResultSettingsData] =
    useState<ResultSettingsType | null>(null);
  const [scopeContentData, setScopeContentData] =
    useState<ScopeContentType | null>(null);
  const [scopeContentChanged, setScopeContentChanged] =
    useState<boolean>(false);
  const [termsContentData, setTermsContentData] = useState<Terms[]>([]);
  const [termsContentChanged, setTermsContentChanged] =
    useState<boolean>(false);
  const [apiReceivedTermsContentData, setApiReceivedTermsContentData] =
    useState<Terms[]>([]);
  const [isDraftLoading, setIsDraftLoading] = useState<boolean>(false);
  const [isRequestFailed, setIsRequestFailed] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [isEditFlow, setIsEditFlow] = useState<boolean>(false);
  const initialScopeContentSavedData = useRef<ScopeContentType | null>(null);
  const initialTermListContentSavedData = useRef<Terms[]>([]);
  const initialResultSettingData = useRef<ResultSettingsType | null>(null);
  const [reportStatus, setReportStatus] =
    useState<STRTermRunResultsType | null>(null);

  const {showToast} = useToast();
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {projectId} = useSelector((state: RootState) => state.reports);
  const {currentFieldTemplateId, currentStrReportId, relationalGroupFields} =
    useSelector((state: RootState) => state.STR);

  const disableNextButton = useMemo(() => {
    setIsRequestFailed(false);
    switch (activeStep) {
      case STR_STEPS.SCOPE_CONTENT:
        if (
          scopeContentData &&
          scopeContentData.reportName.trim() !== '' &&
          scopeContentData.reportName.length <= MAX_CHARACTER_COUNT &&
          scopeContentData.isValid
        )
          return false;
        return true;
      case STR_STEPS.ADD_TERMS:
        if (termsContentData.length > 0) return false;
        return true;
      case STR_STEPS.RESULT_SETTING:
        if (resultSettingsData?.isValid) return false;
        return true;
      default:
        return false;
    }
  }, [activeStep, scopeContentData, resultSettingsData, termsContentData]);

  const disableSaveReportButton = useMemo(() => {
    switch (activeStep) {
      case STR_STEPS.SCOPE_CONTENT:
        if (
          scopeContentData &&
          scopeContentData.reportName.trim() !== '' &&
          scopeContentData.reportName.length <= MAX_CHARACTER_COUNT &&
          scopeContentData.isValid
        )
          return false;
        return true;
      case STR_STEPS.RESULT_SETTING:
        return !resultSettingsData?.isValid;
      default:
        return false;
    }
  }, [activeStep, resultSettingsData, scopeContentData]);

  const isUnsavedChangeModalVisible = useMemo(() => {
    if (currentFlow === STR_FLOW.EDIT) {
      return (
        !_.isEqual(scopeContentData, initialScopeContentSavedData.current) ||
        !_.isEqual(termsContentData, initialTermListContentSavedData.current) ||
        !_.isEqual(resultSettingsData, initialResultSettingData.current)
      );
    } else {
      switch (activeStep) {
        case STR_STEPS.SCOPE_CONTENT:
          return !_.isEqual(
            scopeContentData,
            initialScopeContentSavedData.current
          );
        case STR_STEPS.ADD_TERMS:
          return !_.isEqual(
            termsContentData,
            initialTermListContentSavedData.current
          );
        case STR_STEPS.RESULT_SETTING:
          return !_.isEqual(
            resultSettingsData,
            initialResultSettingData.current
          );
        default:
          return false;
      }
    }
  }, [
    scopeContentData,
    termsContentData,
    resultSettingsData,
    initialResultSettingData,
    initialTermListContentSavedData,
    initialScopeContentSavedData,
    activeStep,
    isDraftLoading,
  ]);

  const reviewData: STRCreateReviewDataType = useMemo(() => {
    const settings: string[] = [];
    if (resultSettingsData?.includeUniqueHitsFlag)
      settings.push(t('eca.searchTermReport.resultSetting.includeUniqueHit'));

    if (resultSettingsData?.storeHitQueryStringsFlag)
      settings.push(t('eca.searchTermReport.resultSetting.storeHitQuery'));

    const selectedFieldIds = resultSettingsData?.relationalGroupFieldList ?? [];
    const relationalGroups: string[] = (relationalGroupFields ?? [])
      .filter((item) => selectedFieldIds.includes(item.id.toString()))
      .map((item) => item.name);

    return {
      name: scopeContentData?.reportName ?? '',
      index: scopeContentData?.selectedIndex ?? '',
      notes: scopeContentData?.customNote ?? '',
      type: 'Report only',
      settings: settings,
      relationalGroups: relationalGroups,
      emailRecipients: resultSettingsData?.emailList ?? [],
      searchableSet: scopeContentData?.selectedSearch?.label ?? '',
    };
  }, [resultSettingsData, scopeContentData, relationalGroupFields]);

  const loadReportStatusAsync = async (
    clientId: string,
    projectId: string,
    fieldTemplateId: number,
    reportId: string
  ) => {
    const queryParams = {
      clientId: clientId,
      projectId: projectId,
    };
    const queryString = new URLSearchParams(queryParams).toString();

    const result = await getSearchTermRunResults(
      fieldTemplateId?.toString() ?? '',
      reportId,
      queryString
    );
    const data: STRTermRunResultsType[] = result.data.data;
    setReportStatus(data[0]);
  };

  useEffect(() => {
    loadReportStatusAsync(
      clientId,
      projectId ?? '',
      currentFieldTemplateId ?? 0,
      currentStrReportId ?? ''
    );
  }, [clientId, projectId, currentFieldTemplateId, currentStrReportId]);

  const saveSTRData = async () => {
    const getStage = () => {
      if (currentFlow === STR_FLOW.ADDTERMS || currentFlow === STR_FLOW.EDIT) {
        return 'Ready';
      } else {
        switch (activeStep) {
          case STR_STEPS.SCOPE_CONTENT:
            return 'Scope';
          case STR_STEPS.ADD_TERMS:
            return 'Terms';
          case STR_STEPS.RESULT_SETTING:
            return 'Result_Settings';
          case STR_STEPS.RUN_SEARCH:
            return 'Ready';
          default:
            return 'Scope';
        }
      }
    };
    const getDraftFlag = () => {
      if (currentFlow === STR_FLOW.ADDTERMS || currentFlow === STR_FLOW.EDIT)
        return false;
      return true;
    };
    try {
      const isReadyForNextStep =
        projectId &&
        clientId &&
        scopeContentData?.fieldTemplateId &&
        scopeContentData?.isValid;
      if (isReadyForNextStep) {
        setIsDraftLoading(true);
        const response = await createSTR(
          scopeContentData?.fieldTemplateId,
          clientId,
          projectId,
          {
            strReportId: currentStrReportId,
            isDraftFlag: getDraftFlag(),
            stage: getStage(),
            scope: {
              name: scopeContentData?.reportName ?? '',
              indexName: scopeContentData?.selectedIndex,
              fieldTemplateId: scopeContentData?.fieldTemplateId,
              searchId:
                extractSelectedNodeId(
                  scopeContentData?.selectedSearch?.id ?? ''
                ) || undefined,
              notes: scopeContentData?.customNote,
            },
            terms:
              termsContentData?.map((item: Terms, index: number) => ({
                term: wrapQueryBeforeSymbol(item.term.trim()),
                highlightColor: item.backgroundColor,
                termId: item.termId ?? undefined,
                SortOrder: index + 1,
              })) ?? [],
            resultSettings: {
              includeUniqueHitsFlag:
                resultSettingsData?.includeUniqueHitsFlag ?? false,
              storeHitQueryStringsFlag:
                resultSettingsData?.storeHitQueryStringsFlag ?? false,
              emailNotificationList: resultSettingsData?.emailList,
              relationalGroupFieldList:
                resultSettingsData?.relationalGroupFieldList ?? [],
            },
          }
        );
        if (currentFlow === STR_FLOW.EDIT) {
          const termsContentDataAPIReceived = (
            response.data.data?.[0]?.terms ?? []
          )
            .filter((item: Terms) => !_.isEmpty(item))
            .map((term: Terms) => ({
              ...term,
              term: unwrapQueryText(term.term),
            }));
          setTermsContentData(
            updateTermIds(termsContentDataAPIReceived, termsContentData)
          );
          setApiReceivedTermsContentData(termsContentDataAPIReceived);
          initialScopeContentSavedData.current = scopeContentData;
          initialTermListContentSavedData.current = updateTermIds(
            termsContentDataAPIReceived,
            termsContentData
          );
          initialResultSettingData.current = resultSettingsData;
          await loadReportStatusAsync(
            clientId,
            projectId,
            currentFieldTemplateId ?? 0,
            currentStrReportId ?? ''
          );
        } else {
          switch (activeStep) {
            case STR_STEPS.SCOPE_CONTENT:
              initialScopeContentSavedData.current = scopeContentData;
              break;
            case STR_STEPS.ADD_TERMS:
              initialTermListContentSavedData.current = termsContentData;
              break;
            case STR_STEPS.RESULT_SETTING:
              initialResultSettingData.current = resultSettingsData;
              break;
            default:
              break;
          }
        }
        showToast(
          t('eca.searchTermReport.saveSTRSuccessMessage', {
            strName: scopeContentData?.reportName,
          }),
          'success',
          undefined,
          'center'
        );
        dispatch(setStrReportId(response.data.data[0].strReportId));
        return true;
      } else {
        showToast(
          t('eca.searchTermReport.saveSTRErrorMessage', {
            strName: scopeContentData?.reportName,
          }),
          'error',
          undefined,
          'center'
        );
        return false;
      }
    } catch (error: unknown) {
      showToast(
        t('eca.searchTermReport.saveSTRErrorMessage', {
          strName: scopeContentData?.reportName,
        }),
        'error',
        undefined,
        'center'
      );
      setIsRequestFailed(true);
      const validationError = getAPIErrorDetail(error).validationErrors;
      const reportNameError = validationError?.find(
        (item) => item.field === 'scope.Name'
      );

      const firstMessage = reportNameError?.message?.[0]?.toLowerCase();

      if (firstMessage?.includes('already exists')) {
        setShowErrorModal(true);
      }

      return false;
    } finally {
      setIsDraftLoading(false);
    }
  };

  const finaliseSTRData = async () => {
    try {
      if (
        projectId &&
        clientId &&
        scopeContentData?.fieldTemplateId &&
        currentStrReportId
      ) {
        setIsDraftLoading(true);
        const response = await finaliseSTR(
          scopeContentData?.fieldTemplateId,
          clientId,
          projectId,
          currentStrReportId
        );
        const isSuccess = response?.data?.data?.isSuccess;
        if (isSuccess) return true;
      } else {
        showToast(
          t('eca.searchTermReport.errorMessage'),
          'error',
          undefined,
          'center'
        );
        return false;
      }
    } catch (error: unknown) {
      showErrorToast(showToast, error, 'center');
      return false;
    } finally {
      setIsDraftLoading(false);
    }
  };
  const handleDeleteSTR = async () => {
    try {
      if (projectId && clientId && currentStrReportId) {
        setIsDraftLoading(true);
        await deleteSTR(
          clientId,
          projectId,
          currentStrReportId,
          scopeContentData?.fieldTemplateId
        );
        navigate(
          `/eci-home-page/?tab=${EciHomePageTabs.STR}`
        );
      } else {
        showToast(
          t('eca.searchTermReport.errorMessage'),
          'error',
          undefined,
          'center'
        );
      }
    } catch (error: unknown) {
      showErrorToast(showToast, error, 'center');
    } finally {
      setIsDraftLoading(false);
    }
  };

  const headingRef = useRef<HTMLHeadingElement>(null);

  const getSTRDetailData = async () => {
    const getCurrentStep = (step: string) => {
      switch (step) {
        case 'Scope':
          return STR_STEPS.SCOPE_CONTENT;
        case 'Terms':
          return STR_STEPS.ADD_TERMS;
        case 'Result_Settings':
          return STR_STEPS.RESULT_SETTING;
        case 'Ready':
          return STR_STEPS.RUN_SEARCH;
        default:
          return STR_STEPS.SCOPE_CONTENT;
      }
    };

    const updateCurrentActiveStep = (flow: STR_FLOW, stage: string) => {
      switch (flow) {
        case STR_FLOW.DRAFT:
          setActiveStep(getCurrentStep(stage));
          break;
        case STR_FLOW.EDIT:
          setIsEditFlow(true);
          setActiveStep(STR_STEPS.SCOPE_CONTENT);
          break;
        case STR_FLOW.ADDTERMS:
          setActiveStep(STR_STEPS.ADD_TERMS);
          break;
      }
    };

    try {
      const isSTRDetailFetchAllowed = (): boolean => {
        const hasBasicInfo = !!projectId && !!clientId && !!currentStrReportId;

        const isEditOrDraftFlowWithTemplate =
          currentFlow !== STR_FLOW.CREATE &&
          currentFieldTemplateId !== undefined;

        const isCreateFlowWithTemplate =
          currentFlow === STR_FLOW.CREATE &&
          scopeContentData?.fieldTemplateId !== undefined;

        return (
          hasBasicInfo &&
          (isEditOrDraftFlowWithTemplate || isCreateFlowWithTemplate)
        );
      };

      if (isSTRDetailFetchAllowed()) {
        setIsDraftLoading(true);
        const fieldTemplateID =
          scopeContentData?.fieldTemplateId ?? currentFieldTemplateId;
        const response = await getSTRDetail(
          fieldTemplateID ?? 0,
          clientId,
          projectId as string,
          currentStrReportId as string
        );
        const data = response?.data?.data;
        if (data.length > 0) {
          const reportData = data[0];
          const termsContentData = (reportData?.terms ?? [])
            .filter((item: Terms) => !_.isEmpty(item))
            .map((term: Terms) => ({
              ...term,
              term: unwrapQueryText(term.term),
            }));
          const resultSettingData = reportData.resultSettings;
          const scopeContentData = {
            reportName: reportData.scope.name,
            customNote: reportData.scope.notes,
            selectedIndex: reportData.scope.indexName,
            selectedSearch:
              reportData.scope?.searchId && reportData.scope?.searchId !== ''
                ? {
                    id: reportData.scope.searchId,
                    label: reportData.scope.savedSearchName,
                  }
                : null,
            fieldTemplateId: reportData.scope.fieldTemplateId,
            isValid: true,
          };
          initialScopeContentSavedData.current = scopeContentData;
          setScopeContentData(scopeContentData);
          if (
            !(
              termsContentData.length === 0 ||
              !Object.keys(termsContentData[0]).length
            )
          ) {
            const termListData = termsContentData.map(
              (
                term: {highlightColor: string; term: string; termId?: string},
                index: number
              ) => ({
                id: index + 1,
                textColor: colorArray.find(
                  (obj) => obj.backgroundColor === term.highlightColor
                )?.textColor,
                backgroundColor: term.highlightColor,
                term: term.term,
                termId: term.termId ?? undefined,
              })
            );
            initialTermListContentSavedData.current = termListData;
            setTermsContentData(termListData);
            setApiReceivedTermsContentData(termListData);
          }
          const resultdata = {
            includeUniqueHitsFlag: resultSettingData.includeUniqueHitsFlag,
            storeHitQueryStringsFlag:
              resultSettingData.storeHitQueryStringsFlag,
            relationalGroupFieldList:
              resultSettingData.relationalGroupFieldList,
            emailList: resultSettingData.emailNotificationList.flatMap(
              (item: string) => item.split(',')
            ),
            isValid: true,
          };
          initialResultSettingData.current = resultdata;
          setResultSettingsData(resultdata);

          updateCurrentActiveStep(currentFlow as STR_FLOW, reportData.stage);
          return data;
        }
      } else {
        showToast(
          t('eca.searchTermReport.errorMessage'),
          'error',
          undefined,
          'center'
        );
      }
    } catch (error: unknown) {
      showErrorToast(showToast, error, 'center');
    } finally {
      setIsDraftLoading(false);
    }
  };

  const runTermsReportAndUpdateUI = async (runAllTerms: boolean) => {
    try {
      setIsDraftLoading(true);

      // Call the API to run the STR report
      const fieldTemplateId = (
        scopeContentData?.fieldTemplateId ?? ''
      ).toString();
      const reportId = currentStrReportId ?? '';
      const apiQueryParams = {
        clientId: clientId,
        projectId: projectId ?? '',
      };
      const strApiQueryParams = new URLSearchParams(apiQueryParams).toString();
      await runSTRTerms(fieldTemplateId, reportId, strApiQueryParams, {
        runAllTermsFlag: runAllTerms,
      });

      // Show success notification
      let successMessage = t(
        'eca.searchTermReport.viewSTR.allTermRunSuccessMsg'
      );
      if (!runAllTerms) {
        successMessage = t(
          'eca.searchTermReport.viewSTR.pendingTermRunSuccessMsg'
        );
      }
      showToast(successMessage, 'success', undefined, 'right');

      // Navigate to the View STR page
      const pageUrl = navConstants.str.viewSTR;
      const pageQueryParams = {
        fieldTemplateId: fieldTemplateId,
        id: reportId,
        name: scopeContentData?.reportName ?? '',
      };
      const strPageQueryParams = new URLSearchParams(
        pageQueryParams
      ).toString();
      navigate(`${pageUrl}?${strPageQueryParams}`);
    } catch (e) {
      showErrorToast(showToast, e);
    } finally {
      setIsDraftLoading(false);
    }
  };

  const handleTermsWorkflow = async (runAllTerms: boolean) => {
    const isReady = await saveSTRData();
    if (!isReady) {
      return;
    }

    const detailData = await getSTRDetailData();
    if (detailData?.[0] && !detailData[0].isDraftFlag) {
      await runTermsReportAndUpdateUI(runAllTerms);
    } else {
      const isFinalized = await finaliseSTRData();
      if (isFinalized) {
        await runTermsReportAndUpdateUI(runAllTerms);
      }
    }
  };

  const runTermsHandler = async (runAllTerms: boolean) => {
    if (termsContentData.length > 0) {
      await handleTermsWorkflow(runAllTerms);
    } else {
      handleEmptyTermsFlow();
    }
  };

  const runAllTermsHandler = async () => {
    await runTermsHandler(true);
  };

  const runPendingTermsHandler = async () => {
    await runTermsHandler(false);
  };

  useEffect(() => {
    switch (currentFlow) {
      case STR_FLOW.ADDTERMS: {
        getSTRDetailData();
        break;
      }
      case STR_FLOW.DRAFT: {
        getSTRDetailData();
        break;
      }
      case STR_FLOW.EDIT: {
        getSTRDetailData();
        break;
      }
      case STR_FLOW.CREATE:
      default: {
        dispatch(setStrReportId(undefined));
        dispatch(setCurrentFieldTemplateId(undefined));
        setActiveStep(STR_STEPS.SCOPE_CONTENT);
        break;
      }
    }
  }, []);

  useEffect(() => {
    const wrapper = document.querySelector<HTMLElement>('.osano-cm-window');
    wrapper?.setAttribute('role', 'region');
    wrapper?.setAttribute('aria-label', t('common.footer.cookiePreferences'));
  }, []);

  const modalCancelHandler = () => setShowConfirmationModal(false);
  const okActionHandler = async () => {
    const isSuccess = await saveSTRData();
    if (isSuccess) {
      if (currentFlow === STR_FLOW.ADDTERMS) {
        navigate(-1);
      } else {
        navigate(
          `/eci-home-page/?tab=${EciHomePageTabs.STR}`
        );
      }
    } else {
      setShowConfirmationModal(false);
    }
  };
  const setIsLoading = (isLoading: boolean) => {
    setIsDraftLoading(isLoading);
  };
  const strCancelHandler = (
    e?:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement>
  ) => {
    e?.preventDefault();
    if (!disableNextButton && isUnsavedChangeModalVisible) {
      setShowConfirmationModal(true);
    } else if (currentFlow === STR_FLOW.ADDTERMS) {
      navigate(-1);
    } else {
      navigate(
        `/eci-home-page/?tab=${EciHomePageTabs.STR}`
      );
    }
  };

  const nextButtonHandler = async () => {
    const isSuccess = await saveSTRData();
    if (isSuccess) setActiveStep(activeStep + 1);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      subPage: {
        subpage_name: steps[activeStep]?.label,
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
      click: {
        name: t('gaTracking.global.next'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.global.nextStepAction', {
          stepName: steps[activeStep]?.label,
        }),
      },
    });
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: steps[activeStep + 1]?.label,
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    setTimeout(() => {
      headingRef.current?.focus();
      headingRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 0);
  };

  const saveReportHandler = () => {
    saveSTRData();
    const gaLabel = t(stringPath + 'SaveReport');
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: gaLabel,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.str.saveReportAction'),
      },
      subPage: {
        subpage_name: steps[activeStep]?.label,
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };

  const saveAsDraftHandler = async () => {
    const isSuccess = await saveSTRData();
    if (isSuccess) {
      const isFinaliseSuccess = await finaliseSTRData();
      if (isFinaliseSuccess)
        navigate(
          `/eci-home-page/?tab=${EciHomePageTabs.STR}`
        );
    }
  };

  const errorDialogOkHandler = () => {
    setShowErrorModal(false);
  };

  const handleEmptyTermsFlow = () => {
    showToast(
      t('eca.searchTermReport.termsEmptyError'),
      'error',
      undefined,
      'center'
    );
    setActiveStep(1);
  };

  const saveButtonHandler = async () => {
    if (termsContentData?.length > 0) {
      const isSuccess = await saveSTRData();
      if (isSuccess) {
        if (currentFlow === STR_FLOW.ADDTERMS) navigate(-1);
      }
    } else {
      handleEmptyTermsFlow();
    }
    const gaLabel =
      currentFlow === STR_FLOW.ADDTERMS
        ? t('gaTracking.str.save')
        : t('gaTracking.str.saveReport');
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: gaLabel,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.str.saveReportAction'),
      },
      subPage: {
        subpage_name: steps[activeStep]?.label,
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };

  const getHeaderText = () => {
    if (currentFlow === STR_FLOW.ADDTERMS) {
      return t(stringPath + 'addMoreTerms');
    } else if (currentFlow === STR_FLOW.EDIT) {
      return t(stringPath + 'editSTR');
    } else {
      return t(stringPath + 'createSTR');
    }
  };

  const onScopeChanged = (data: ScopeContentType) => {
    let hasScopeChanged = false;
    const initialScopeContent = initialScopeContentSavedData.current;

    if (
      (initialScopeContent?.selectedIndex &&
        data.selectedIndex &&
        initialScopeContent?.selectedIndex !== data.selectedIndex) ||
      (initialScopeContent?.selectedSearch &&
        data.selectedSearch &&
        initialScopeContent?.selectedSearch !== data.selectedSearch)
    ) {
      // Index has changed
      hasScopeChanged = true;
    }
    setScopeContentChanged(hasScopeChanged);

    setScopeContentData(data);
  };

  const onTermsChanged = (data: Terms[]) => {
    let hasTermsChanged = false;
    const initialTermsContent = initialTermListContentSavedData.current;
    if (
      initialTermsContent &&
      data &&
      initialTermsContent.length !== data.length
    ) {
      // Term was added or removed
      hasTermsChanged = true;
    } else {
      // Check if a term was modified
      for (const initialTerm of initialTermsContent) {
        if (!data.find((term) => term.term === initialTerm.term)) {
          hasTermsChanged = true;
          break;
        }
      }
    }
    setTermsContentChanged(hasTermsChanged);

    setTermsContentData(data);
  };

  const shouldShowRunPendingButton = () => {
    // If the scope has changed, only show "Run All Terms"
    if (scopeContentChanged) {
      return false;
    }

    // If terms have changed, show "Run Pending Terms"
    if (termsContentChanged) {
      return true;
    }

    // If the report status is "PENDING", show "Run Pending Terms"
    const hasTermsPending = reportStatus?.hasPendingTerms;
    if (hasTermsPending) {
      return true;
    }

    return false;
  };

  return (
    <div className="create-str-main">
      <div className="create-str-header">
        <AurBreadcrumb
          items={[
            {
              label: t(stringPath + 'STR'),
              path: '#',
              onClick: strCancelHandler,
            },
          ]}
        />
        <Typography
          className="typo-text headline1_size title"
          tabIndex={-1}
          ref={headingRef}
          component="h1"
        >
          {getHeaderText()}
        </Typography>
        <AurStepper
          activeStep={activeStep}
          steps={steps}
          setActiveStep={(index: number) => {
            setActiveStep(index);
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              subPage: {
                subpage_name: steps[activeStep]?.label,
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
              click: {
                name: steps[index]?.label,
                type: GA_CLICK_TYPES.TAB,
                region: GA_REGIONS.TAB,
                action: t('gaTracking.global.previousStepAction', {
                  stepName: steps[index]?.label,
                }),
              },
            });
            trackGAEvent({
              eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
              subPage: {
                subpage_name: steps[index]?.label,
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });
          }}
          isEditFlow={
            currentFlow === STR_FLOW.ADDTERMS || currentFlow === STR_FLOW.EDIT
          }
        />
      </div>
      <div className="create-str-mid">
        {activeStep === STR_STEPS.SCOPE_CONTENT ? (
          <ScopeContent
            initialData={scopeContentData}
            onDataChange={onScopeChanged}
            isEditFlow={isEditFlow}
          />
        ) : null}
        {activeStep === STR_STEPS.ADD_TERMS ? (
          <TermsContent
            initialData={termsContentData}
            onDataChange={onTermsChanged}
            initialAPIResponseData={apiReceivedTermsContentData}
            clientId={clientId}
            projectId={projectId}
            scopeContentData={scopeContentData}
            setIsLoading={setIsLoading}
          />
        ) : null}
        {activeStep === STR_STEPS.RESULT_SETTING ? (
          <ResultSetting
            initialData={resultSettingsData}
            onDataChange={setResultSettingsData}
          />
        ) : null}
        {activeStep === STR_STEPS.RUN_SEARCH ? (
          <ReviewSearch
            reviewData={reviewData}
            saveAsDraftHandler={saveAsDraftHandler}
            deleteSTR={handleDeleteSTR}
            isEditFlow={isEditFlow}
            onRunAllTermClick={runAllTermsHandler}
            shouldShowRunPendingButton={shouldShowRunPendingButton()}
            onRunAllPendingTermsClick={runPendingTermsHandler}
          />
        ) : null}
      </div>
      {activeStep < STR_STEPS.RUN_SEARCH && (
        <div className="create-str-end">
          <AurButton
            label={t(stringPath + 'cancel')}
            type="exit"
            size="Medium"
            onClick={strCancelHandler}
            dataTestId="cancel"
          />
          <div className="right-button-container">
            {currentFlow === STR_FLOW.ADDTERMS ||
            currentFlow === STR_FLOW.EDIT ? (
              <AurButton
                label={
                  currentFlow === STR_FLOW.ADDTERMS
                    ? t(stringPath + 'save')
                    : t(stringPath + 'SaveReport')
                }
                type="continue"
                size="Medium"
                dataTestId="save"
                onClick={saveButtonHandler}
                disabled={disableSaveReportButton}
              />
            ) : (
              <>
                <AurButton
                  label={t(stringPath + 'SaveReport')}
                  type="exit"
                  size="Medium"
                  disabled={disableSaveReportButton}
                  dataTestId="saveReport"
                  onClick={saveReportHandler}
                />
                <AurButton
                  label={t(stringPath + 'Next')}
                  type="continue"
                  size="Medium"
                  disabled={disableNextButton || isRequestFailed}
                  onClick={nextButtonHandler}
                  dataTestId="Next"
                />
              </>
            )}
          </div>
        </div>
      )}
      <AurPopup
        onOk={okActionHandler}
        onCancel={modalCancelHandler}
        open={showConfirmationModal}
        showPopupTitle={true}
        popupTitle={t(stringPath + 'unSavedChangesModal.popupTitle')}
        showCloseIcon={true}
        showPopupHeading={true}
        popupHeading={t(stringPath + 'unSavedChangesModal.popupHeading')}
        contentType="text"
        popupContent={t(stringPath + 'unSavedChangesModal.popupContent')}
        labelActionCancel={t(stringPath + 'cancel')}
        labelActionOk={t(stringPath + 'ok')}
        className="create-str-modal aur-unsaved-changes-popup"
      />
      <AurPopup
        className="inactivity-popup-modal error-modal custom-tag-dialog-model"
        open={showErrorModal}
        showPopupTitle={true}
        showPopupHeading={true}
        showCancelOption={false}
        showCloseIcon={false}
        headingIcon={
          <AurIcon
            icon={IconNames.Error}
            color={IconColors.Critical}
            customClassName="heading-warning-icon"
          />
        }
        popupHeading={t(stringPath + 'errorModal.title')}
        popupContent={t(stringPath + 'errorModal.content')}
        labelActionOk={t('common.okGotIt')}
        aria-label={t(stringPath + 'errorModal.title')}
        aria-description={t(stringPath + 'errorModal.content')}
        onOk={errorDialogOkHandler}
        onCancel={noop}
      />
      <AurLoadingIndicator overlay={true} isOpen={isDraftLoading} />
    </div>
  );
};

export default CreateSTR;
